import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { BankAccount, BankAccountCreate, BankAccountFilters } from '../types'

const QUERY_KEY = 'bank_accounts'

/**
 * Hook pour récupérer les comptes bancaires d'une résidence
 */
export function useBankAccounts(filters: BankAccountFilters) {
  return useQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: async () => {
      let query = supabase
        .from('bank_accounts')
        .select(`
          *,
          residence_chart_account:residence_chart_accounts(
            id,
            custom_code,
            custom_name,
            chart_account:chart_accounts(id, code, name)
          )
        `)
        .eq('residence_id', filters.residence_id)
        .order('is_primary', { ascending: false })
        .order('created_at', { ascending: true })

      if (filters.is_active !== undefined) {
        query = query.eq('is_active', filters.is_active)
      }

      if (filters.account_type) {
        query = query.eq('account_type', filters.account_type)
      }

      const { data, error } = await query

      if (error) throw error
      return data as BankAccount[]
    },
    enabled: !!filters.residence_id,
  })
}

/**
 * Hook pour récupérer un compte bancaire par ID
 */
export function useBankAccount(id?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bank_accounts')
        .select(`
          *,
          residence_chart_account:residence_chart_accounts(
            id,
            custom_code,
            custom_name,
            chart_account:chart_accounts(id, code, name)
          )
        `)
        .eq('id', id!)
        .single()

      if (error) throw error
      return data as BankAccount
    },
    enabled: !!id,
  })
}

/**
 * Hook pour créer un compte bancaire
 */
export function useCreateBankAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: BankAccountCreate) => {
      // 1. Créer le compte 512 associé
      const { data: account512Id, error: account512Error } = await supabase.rpc(
        'create_bank_account_512',
        {
          p_residence_id: data.residence_id,
          p_label: data.label,
        }
      )

      if (account512Error) throw account512Error

      // 2. Si is_primary = true, retirer is_primary des autres comptes
      if (data.is_primary) {
        await supabase
          .from('bank_accounts')
          .update({ is_primary: false })
          .eq('residence_id', data.residence_id)
          .eq('is_primary', true)
      }

      // 3. Créer le compte bancaire
      const { data: bankAccount, error: bankError } = await supabase
        .from('bank_accounts')
        .insert({
          residence_id: data.residence_id,
          residence_chart_account_id: account512Id as string,
          label: data.label,
          account_type: data.account_type,
          iban: data.iban,
          bic: data.bic,
          bank_name: data.bank_name,
          account_holder: data.account_holder,
          is_primary: data.is_primary ?? false,
          is_active: true,
        })
        .select()
        .single()

      if (bankError) throw bankError
      return bankAccount as BankAccount
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, { residence_id: variables.residence_id }],
      })
    },
  })
}

/**
 * Hook pour modifier un compte bancaire
 */
export function useUpdateBankAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<BankAccount> & { id: string }) => {
      // Si on définit ce compte comme principal, retirer is_primary des autres
      if (data.is_primary) {
        const { data: currentAccount } = await supabase
          .from('bank_accounts')
          .select('residence_id')
          .eq('id', id)
          .single()

        if (currentAccount) {
          await supabase
            .from('bank_accounts')
            .update({ is_primary: false })
            .eq('residence_id', currentAccount.residence_id)
            .eq('is_primary', true)
            .neq('id', id)
        }
      }

      const { data: result, error } = await supabase
        .from('bank_accounts')
        .update(data)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return result as BankAccount
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

/**
 * Hook pour désactiver un compte bancaire
 */
export function useDeactivateBankAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      // Vérifier qu'il n'y a pas de mouvements en attente
      const { count } = await supabase
        .from('bank_statements')
        .select('*', { count: 'exact', head: true })
        .eq('bank_account_id', id)
        .in('reconciliation_status', ['pending', 'partial'])

      if (count && count > 0) {
        throw new Error(`Impossible de désactiver ce compte : ${count} mouvement(s) en attente de rapprochement`)
      }

      // Désactiver le compte
      const { data, error } = await supabase
        .from('bank_accounts')
        .update({ is_active: false })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as BankAccount
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

/**
 * Hook pour supprimer un compte bancaire
 */
export function useDeleteBankAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('bank_accounts').delete().eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}
